import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  activateSdkApplication,
  createSdkApplication,
  deleteSdkApplication,
  fetchAllSdkApplications,
  fetchAllServiceKeys,
  pauseSdkApplication,
  rotateServiceKey,
  updateSdkApplication,
} from 'js/actions';
import { useGetServiceKeys } from 'new/components/settings/Authentication/ServiceKeys/useGetServiceKeys';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ALL_SDK_APPLICATIONS_REQUEST } from 'js/constants/actionTypes';
import { useGetSdkApplications } from 'new/components/settings/SdkApplications/useGetSdkApplications';
import {
  CreateSdkApplicationPayload,
  UpdateSdkApplicationPayload,
} from 'js/actions/sdkApplications';

export function useSdkApplications() {
  const [error, setError] = useState<Error | null>(null);
  const dispatch = useDispatch<any>();
  const sdkApplications = useGetSdkApplications();
  const allServiceKeys = useGetServiceKeys();
  const isLoading = useIsLoading(GET_ALL_SDK_APPLICATIONS_REQUEST);

  const load = async () => {
    try {
      await dispatch(fetchAllSdkApplications());
      await dispatch(fetchAllServiceKeys());
    } catch (e) {
      setError(e);
      console.error(`Error at fetchAllSdkApplications: ${e}`);
    }
  };

  const create = async (data: CreateSdkApplicationPayload) => {
    if (!data) return null;
    try {
      const response = await dispatch(createSdkApplication(data));
      if (response.result) {
        load();
      }
    } catch (e) {
      setError(e);
    }
  };

  const update = async (id: number, data: UpdateSdkApplicationPayload) => {
    if (!id || !data) return null;
    try {
      await dispatch(updateSdkApplication(id, data));
      load();
    } catch (e) {
      setError(e);
    }
  };

  const remove = async (id: number) => {
    if (!id) return null;
    try {
      await dispatch(deleteSdkApplication(id));
    } catch (e) {
      setError(e);
    }
  };

  const pause = async (id: number) => {
    if (!id) return null;
    try {
      await dispatch(pauseSdkApplication(id));
    } catch (e) {
      setError(e);
    }
  };

  const activate = async (id: number) => {
    if (!id) return null;
    try {
      await dispatch(activateSdkApplication(id));
    } catch (e) {
      setError(e);
    }
  };

  const rotateLinkedKeys = async (appId: number) => {
    const linkedKeys = (allServiceKeys ?? []).filter((key) => key?.sdk_application_id === appId);
    try {
      await Promise.all(linkedKeys.map((key) => dispatch(rotateServiceKey(key.id))));
      load();
    } catch (e) {
      setError(e);
    }
  };

  useEffect(() => {
    load();
  }, []);

  return {
    data: sdkApplications,
    error,
    isLoading,
    load,
    create,
    update,
    remove,
    pause,
    activate,
    rotateLinkedKeys,
  };
}
